import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type { OutputColumnWithMetadata } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { Bounds, TableClient } from '@thinkalpha/table-client';
import type { Observable, ObservedValueOf } from 'rxjs';
import type { Snapshot } from 'src/api/getTableSnapshot';
import type { TableStatus } from 'src/components/table-view/model';
import type { ReactiveInjectable } from 'src/features/ioc';
import type { FullKeyType } from 'src/types';

/**
 * How to request a subscription (and possibly creation) of a table.
 */
export type TableCreationQuery = QueryObserverOptions<any, any, FullKeyType | undefined>;

/**
 * Payload which is able to initialize a TableModel. Prefer the `Signal` form of `queryOptions`.
 */
export interface TableCreationOptions {
    initBounds?: Bounds;
    initThrottle?: number;
    /**
     * Descriptive name shown in Redux DevTools (e.g. `"Screener/results"`).
     * The final instance name will be `"TableModel: {devtoolsName} [{shortId}]"`.
     */
    devtoolsName?: string;
    /**
     * Prefer the `Signal` form of `queryOptions` for reactivity. The `setQueryOptions` imperative form is deprecated.
     */
    queryOptions: TableCreationQuery | Signal<TableCreationQuery>;
}

export interface SignalUpdate {
    update: ObservedValueOf<TableClient['update$']> | undefined;
}

export const NEW_TABLE_RESET = Symbol('NEW_TABLE_RESET');
export type NEW_TABLE_RESET = typeof NEW_TABLE_RESET;

export interface TableModel extends ReactiveInjectable {
    /**
     * The current bounds configuration of the TableModel
     */
    readonly bounds: Bounds;

    columns: OutputColumnWithMetadata[];
    columnsSignal: ReadonlySignal<OutputColumnWithMetadata[]>;
    /**
     * A signal that represents the current connection status of the table
     */
    connectionStatus: Signal<TableStatus>;

    count$: Observable<number>;
    /**
     * A signal that represents the current row count of the table
     */
    countSignal: ReadonlySignal<number>;
    /**
     * A signal that tracks the number of table creation failures
     */
    failureCount: ReadonlySignal<number>;

    /**
     * Removes an existing table subscription
     */
    destroy(): void;

    error: Error | undefined;

    getColumnById(columnId: string): OutputColumnWithMetadata | null;

    getColumnUpdate$<T = unknown>(columnId: string): Observable<T>;

    init(tableCreationOptions: TableCreationOptions): void;

    /**
     * Manually triggers a refetch of the table creation query.
     * Useful for recovering from errors, particularly 400-series client errors
     * that do not auto-retry.
     */
    refetch(): void;

    key: string | null;
    /**
     * The current table key as a signal. Empty until a table client is attached.
     */
    readonly keySignal: ReadonlySignal<string>;
    /**
     * A signal that represents the current table construction progress percentage (0-100)
     * Will be undefined when no table is being constructed or when progress isn't available
     */
    progress: Signal<number | undefined>;
    /**
     * An observable that emits the current table construction progress percentage (0-100)
     * Will emit undefined when no table is being constructed or when progress isn't available
     */
    progress$: Observable<number | undefined>;
    /**
     * The server UUID (tsId) for this table connection
     */
    serverUUID: string | null;

    setBounds(bounds: Bounds): void;

    /**
     * Set a descriptive name for this TableModel in Redux DevTools.
     * The instance name becomes `"TableModel: {name} [{shortId}]"`.
     */
    setDevtoolsName(name: string): void;

    setThrottle(throttle: number | undefined, getMoreData: boolean): void;

    snapshot(opts: { firstRow?: number; windowSize?: number }): Promise<Snapshot | null>;

    waitForTableConnection(): Promise<void>;

    sortable: boolean;
    /**
     * @deprecated - TODO: Remove before PR merge if possible
     */
    tableClient: TableClient | null;

    /**
     * @deprecated - The latest cookie that was sent to the proxy
     */
    readonly latestCookie: string | undefined;

    /**
     * The current throttle configuration of the TableModel
     */
    readonly throttle: number | undefined;
    update$: TableClient['update$'];
    updates$: TableClient['updates$'];
    updateSignal: ReadonlySignal<SignalUpdate>;
    updateWithReset$: Observable<ObservedValueOf<TableClient['update$']> | NEW_TABLE_RESET>;
    updatesWithReset$: Observable<ObservedValueOf<TableClient['updates$']> | NEW_TABLE_RESET>;

    /**
     * @deprecated Prefer the Signal form of `queryOptions` in `init()` for reactivity.
     * This imperative API exists for legacy code that cannot easily adopt signals.
     */
    updateQueryOptions(queryOptions: QueryObserverOptions<any, any, FullKeyType>): void;
}

export * from './optimization';
